import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import {
  ArrowLeftIcon,
  PencilSquareIcon,
  EnvelopeIcon,
  PhoneIcon,
} from '@heroicons/react/24/outline';
import { useToast } from '../hooks/useToast';
import {
  IndividualMemberDto,
  Gender,
  MembershipType,
  getMemberDisplayFirstName,
  getMemberDisplayLastName,
} from '../types/member';
import { adminService } from '../services/admin';
import { resolveVignetteStatus } from '../utils/vignette';
import { formatImportProblems } from '../utils/importProblems';
import { tryNormalizeEnumValue } from '../utils/enumNormalize';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { Avatar } from '../components/common/Avatar';
import { VignetteStatusBadge } from '../components/admin/VignetteStatusBadge';
import ProfessionalProfileDisplay from '../components/profile/ProfessionalProfileDisplay';
import { Card } from '../design-system/components/Card';
import { Button } from '../design-system/components/Button';
import { Heading, Text } from '../design-system/components/Typography';
import { spacing } from '../design-system/tokens/spacing';
import { typography } from '../design-system/tokens/typography';
import { borders } from '../design-system/tokens/borders';
import { breakpoints } from '../design-system/tokens/breakpoints';
import { animations } from '../design-system/tokens/animations';
import { headerWatermarkStyles } from '../components/dashboard/dashboardWatermark';
import { MonumentCaption } from '../components/dashboard/MonumentCaption';
import { useMonumentWatermark } from '../hooks/useMonumentWatermark';
import { parseMemberCardNumber } from '../utils/memberCardNumber';

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[6]};
  width: 100%;
  max-width: 1180px;
`;

const IdentityCard = styled.header`
  position: relative;
  isolation: isolate;
  overflow: hidden;
  min-height: 180px;
  display: flex;
  align-items: center;
  padding: ${spacing[8]};
  margin-bottom: ${spacing[2]};
  border-radius: ${borders.radius.lg};
  background: transparent;
  ${headerWatermarkStyles}

  @media (max-width: ${breakpoints.md}) {
    padding: ${spacing[5]} ${spacing[4]};
    min-height: auto;
  }
`;

const AccentBar = styled.span`
  display: block;
  width: 58px;
  height: 3px;
  border-radius: 2px;
  background: ${props => props.theme.colors.accent};
  margin-bottom: ${spacing[4]};
`;

const IdentityInner = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${spacing[5]};
  width: 100%;
`;

const IdentityMain = styled.div`
  display: flex;
  gap: ${spacing[4]};
  min-width: 0;
  flex: 1;
`;

const IdentityText = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
  min-width: 0;
`;

const Name = styled.h1`
  margin: 0;
  font-family: ${typography.fontFamily.display};
  font-size: clamp(1.5rem, 2.2vw, 2rem);
  font-weight: ${typography.fontWeight.semibold};
  line-height: 1.2;
  color: ${props => props.theme.colors.text};
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[3]} ${spacing[4]};
  align-items: center;
`;

const MetaItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[2]};
  color: ${props => props.theme.colors.textSecondary};
  font-size: ${typography.fontSize.sm};
  font-family: ${typography.fontFamily.primary};

  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }
`;

const BadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[2]};
  margin-top: ${spacing[1]};
`;

const SoftBadge = styled.span<{ $tone?: 'warning' | 'neutral' | 'accent' }>`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: ${borders.radius.md};
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.semibold};
  letter-spacing: ${typography.letterSpacing.wide};
  text-transform: uppercase;
  background: ${props => {
    if (props.$tone === 'warning') return `${props.theme.colors.warning}18`;
    if (props.$tone === 'accent') return `${props.theme.colors.primary}12`;
    return props.theme.colors.surfaceHover;
  }};
  color: ${props => {
    if (props.$tone === 'warning') return props.theme.colors.warning;
    if (props.$tone === 'accent') return props.theme.colors.primary;
    return props.theme.colors.textSecondary;
  }};
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[2]};
  align-items: center;
`;

const Layout = styled.div`
  display: grid;
  grid-template-columns: 220px 1fr;
  gap: ${spacing[6]};
  align-items: start;

  @media (max-width: ${breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const SideNav = styled(Card)`
  position: sticky;
  top: ${spacing[6]};
  padding: ${spacing[3]} !important;

  @media (max-width: ${breakpoints.md}) {
    position: relative;
    top: 0;
  }
`;

const NavList = styled.nav`
  display: flex;
  flex-direction: column;
  gap: ${spacing[1]};

  @media (max-width: ${breakpoints.md}) {
    flex-direction: row;
    overflow-x: auto;
    gap: ${spacing[2]};
  }
`;

const NavButton = styled.button<{ $active?: boolean }>`
  width: 100%;
  min-height: 44px;
  padding: ${spacing[3]} ${spacing[4]};
  border: none;
  border-radius: ${borders.radius.md};
  background: ${props => (props.$active ? props.theme.colors.primary : 'transparent')};
  color: ${props => (props.$active ? props.theme.colors.surface : props.theme.colors.text)};
  font-size: ${typography.fontSize.sm};
  font-family: ${typography.fontFamily.primary};
  font-weight: ${props =>
    props.$active ? typography.fontWeight.semibold : typography.fontWeight.medium};
  text-align: left;
  cursor: pointer;
  transition: ${animations.transition(
    ['background-color', 'color'],
    animations.duration.fast,
    animations.easing.easeOut
  )};

  &:hover {
    background: ${props =>
      props.$active ? props.theme.colors.primaryDark : props.theme.colors.surfaceHover};
  }

  @media (max-width: ${breakpoints.md}) {
    width: auto;
    white-space: nowrap;
    flex-shrink: 0;
  }
`;

const SectionCard = styled(Card)``;

const SectionTitle = styled(Heading)`
  margin: 0 0 ${spacing[5]} 0 !important;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: ${spacing[5]};
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[1]};
`;

const InfoLabel = styled(Text)`
  text-transform: uppercase;
  letter-spacing: ${typography.letterSpacing.wide};
  font-size: ${typography.fontSize.xs};
`;

const EmptyValue = styled(Text)`
  font-style: italic;
  color: ${props => props.theme.colors.textMuted};
`;

type ProfileSection = 'personal' | 'professional';

function bilingual(
  en?: string | null,
  gr?: string | null,
  preferGr?: boolean
): string | null {
  const primary = preferGr ? gr || en : en || gr;
  const secondary = preferGr ? en : gr;
  if (!primary && !secondary) return null;
  if (primary && secondary && primary !== secondary) return `${primary} / ${secondary}`;
  return primary || secondary || null;
}

function normalizeProfileSection(section: string | null): ProfileSection {
  if (section === 'professional') return 'professional';
  // Legacy contact/membership query params open Personal.
  return 'personal';
}

const MemberProfile: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { error: showError, success: showSuccess } = useToast();
  const watermark = useMonumentWatermark();
  const [member, setMember] = useState<IndividualMemberDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState<ProfileSection>(() =>
    normalizeProfileSection(searchParams.get('section'))
  );
  const [accountActionOpen, setAccountActionOpen] = useState(false);
  const [accountActionLoading, setAccountActionLoading] = useState(false);
  const preferGr = i18n.language.startsWith('el') || i18n.language === 'gr';

  useEffect(() => {
    setActiveSection(normalizeProfileSection(searchParams.get('section')));
  }, [searchParams]);

  const selectSection = (section: ProfileSection) => {
    setActiveSection(section);
    if (searchParams.has('section')) {
      setSearchParams({});
    }
  };
  useEffect(() => {
    if (id) loadMember();
  }, [id]);

  const loadMember = async () => {
    if (!id) return;
    setLoading(true);
    try {
      setMember(await adminService.getMemberById(id));
    } catch (error) {
      showError(error instanceof Error ? error.message : t('profile.notFound'));
    } finally {
      setLoading(false);
    }
  };

  const handleAccountAction = async () => {
    if (!id || !member) return;
    setAccountActionLoading(true);
    try {
      if (member.isActive !== false) {
        await adminService.deactivateMember(id);
        setMember(prev => (prev ? { ...prev, isActive: false } : null));
        showSuccess(t('admin.member.deactivateSuccess'));
      } else {
        await adminService.reactivateMember(id);
        setMember(prev => (prev ? { ...prev, isActive: true } : null));
        showSuccess(t('admin.member.reactivateSuccess'));
      }
      setAccountActionOpen(false);
    } catch (err) {
      showError(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setAccountActionLoading(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString(
      preferGr ? 'el-GR' : 'en-GB',
      { dateStyle: 'medium' }
    );
  };

  const getGenderLabel = (gender?: Gender | string | number | null) => {
    const normalized = tryNormalizeEnumValue(Gender, gender);
    if (normalized == null) return null;
    switch (normalized) {
      case Gender.Female:
        return t('profile.gender.female');
      case Gender.Male:
        return t('profile.gender.male');
      case Gender.Other:
        return t('profile.gender.other');
      case Gender.PreferNotToSay:
        return t('profile.gender.preferNotToSay');
      default:
        return null;
    }
  };

  const getMembershipTypeLabel = (type?: MembershipType | string | number | null) => {
    const normalized = tryNormalizeEnumValue(MembershipType, type);
    if (normalized == null) return '—';
    const labels: Record<MembershipType, string> = {
      [MembershipType.Regular]: t('membership.regular'),
      [MembershipType.Student]: t('membership.student'),
      [MembershipType.Retired]: t('membership.retired'),
      [MembershipType.Honorary]: t('membership.honorary'),
    };
    return labels[normalized] || '—';
  };

  const renderValue = (value: React.ReactNode) => {
    if (value === null || value === undefined || value === '') {
      return <EmptyValue size="base">—</EmptyValue>;
    }
    return (
      <Text size="base" weight="medium" color="text">
        {value}
      </Text>
    );
  };

  if (loading) return <LoadingSpinner fullPage />;
  if (!member) {
    return (
      <Page>
        <Text>{t('profile.notFound')}</Text>
        <Button variant="outline" onClick={() => navigate('/admin/members')}>
          {t('admin.member.backToMembers')}
        </Button>
      </Page>
    );
  }

  const displayName = `${getMemberDisplayFirstName(member)} ${getMemberDisplayLastName(member)}`.trim();
  const vignetteStatus = resolveVignetteStatus(member);
  const parsedCard = parseMemberCardNumber(member.memberCardNumber);
  const parsedPreviousCard = parseMemberCardNumber(member.previousCardNumber);

  const sections: { id: ProfileSection; label: string }[] = [
    { id: 'personal', label: t('profile.sections.personal') },
    { id: 'professional', label: t('profile.sections.professional') },
  ];

  const renderSection = () => {
    switch (activeSection) {
      case 'personal':
        return (
          <SectionCard variant="elevated" accentBorder="top" pattern="dots" patternIntensity="subtle">
            <SectionTitle variant="h3" as="h2" style={{ fontSize: typography.fontSize.section }}>
              {t('profile.sections.personal')}
            </SectionTitle>
            <Text size="xs" weight="semibold" color="secondary" style={{ marginBottom: spacing[3], textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {t('profile.sections.identity')}
            </Text>
            <InfoGrid>
              <InfoItem>
                <InfoLabel size="xs" color="secondary">{t('common.firstName')}</InfoLabel>
                {renderValue(bilingual(member.name?.firstNameEn, member.name?.firstNameGr, preferGr))}
              </InfoItem>
              <InfoItem>
                <InfoLabel size="xs" color="secondary">{t('common.lastName')}</InfoLabel>
                {renderValue(bilingual(member.name?.lastNameEn, member.name?.lastNameGr, preferGr))}
              </InfoItem>
              <InfoItem>
                <InfoLabel size="xs" color="secondary">{t('profile.genderLabel')}</InfoLabel>
                {renderValue(getGenderLabel(member.gender))}
              </InfoItem>
              <InfoItem>
                <InfoLabel size="xs" color="secondary">{t('profile.dateOfBirth')}</InfoLabel>
                {renderValue(formatDate(member.dateOfBirth))}
              </InfoItem>
            </InfoGrid>

            <Text size="xs" weight="semibold" color="secondary" style={{ margin: `${spacing[6]} 0 ${spacing[3]}`, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {t('profile.sections.contactDetails')}
            </Text>
            <InfoGrid>
              <InfoItem>
                <InfoLabel size="xs" color="secondary">{t('common.email')}</InfoLabel>
                {renderValue(member.email)}
              </InfoItem>
              <InfoItem>
                <InfoLabel size="xs" color="secondary">{t('common.phone')}</InfoLabel>
                {renderValue(member.phone)}
              </InfoItem>
              <InfoItem>
                <InfoLabel size="xs" color="secondary">{t('common.mobile')}</InfoLabel>
                {renderValue(member.mobile)}
              </InfoItem>
              <InfoItem>
                <InfoLabel size="xs" color="secondary">{t('profile.residentialAddress')}</InfoLabel>
                {renderValue(
                  bilingual(member.residential?.addressEn, member.residential?.addressGr, preferGr)
                )}
              </InfoItem>
              <InfoItem>
                <InfoLabel size="xs" color="secondary">{t('profile.residentialCity')}</InfoLabel>
                {renderValue(
                  bilingual(member.residential?.cityEn, member.residential?.cityGr, preferGr)
                )}
              </InfoItem>
              <InfoItem>
                <InfoLabel size="xs" color="secondary">{t('profile.residentialPostalCode')}</InfoLabel>
                {renderValue(
                  bilingual(
                    member.residential?.postalCodeEn,
                    member.residential?.postalCodeGr,
                    preferGr
                  )
                )}
              </InfoItem>
              <InfoItem>
                <InfoLabel size="xs" color="secondary">{t('profile.residentialCountry')}</InfoLabel>
                {renderValue(
                  bilingual(member.residential?.countryEn, member.residential?.countryGr, preferGr)
                )}
              </InfoItem>
            </InfoGrid>

            <Text size="xs" weight="semibold" color="secondary" style={{ margin: `${spacing[6]} 0 ${spacing[3]}`, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {t('profile.sections.membershipDetails')}
            </Text>
            <InfoGrid>
              <InfoItem>
                <InfoLabel size="xs" color="secondary">{t('profile.membershipType')}</InfoLabel>
                {renderValue(getMembershipTypeLabel(member.membershipType))}
              </InfoItem>
              <InfoItem>
                <InfoLabel size="xs" color="secondary">{t('profile.registrationYear')}</InfoLabel>
                {renderValue(member.registrationYear)}
              </InfoItem>
              <InfoItem>
                <InfoLabel size="xs" color="secondary">{t('register.memberCardNumber')}</InfoLabel>
                {renderValue(parsedCard.cardNumber)}
              </InfoItem>
              {parsedCard.firstRegisteredYear != null && (
                <InfoItem>
                  <InfoLabel size="xs" color="secondary">{t('profile.firstRegisteredFromCard')}</InfoLabel>
                  {renderValue(parsedCard.firstRegisteredYear)}
                </InfoItem>
              )}
              {(member.transferFromIcom || member.previousCardNumber) && (
                <>
                  <InfoItem>
                    <InfoLabel size="xs" color="secondary">{t('register.transferFromIcom')}</InfoLabel>
                    {renderValue(member.transferFromIcom)}
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel size="xs" color="secondary">{t('register.previousCardNumber')}</InfoLabel>
                    {renderValue(parsedPreviousCard.cardNumber || member.previousCardNumber)}
                  </InfoItem>
                </>
              )}
              {member.importProblems && (
                <InfoItem style={{ gridColumn: '1 / -1' }}>
                  <InfoLabel size="xs" color="secondary">{t('admin.excelImport.importProblems')}</InfoLabel>
                  <Text size="sm" style={{ whiteSpace: 'pre-wrap', marginTop: spacing[1] }}>
                    {formatImportProblems(member.importProblems, t)}
                  </Text>
                  <Text size="xs" color="secondary" style={{ marginTop: spacing[1] }}>
                    {t('admin.excelImport.importProblemsHint')}
                  </Text>
                </InfoItem>
              )}
              <InfoItem>
                <InfoLabel size="xs" color="secondary">{t('profile.mainCommittee')}</InfoLabel>
                {renderValue(member.mainCommittee)}
              </InfoItem>
              <InfoItem>
                <InfoLabel size="xs" color="secondary">{t('profile.additionalCommittees')}</InfoLabel>
                {renderValue(
                  member.additionalCommittees && member.additionalCommittees.length > 0
                    ? member.additionalCommittees.join(', ')
                    : null
                )}
              </InfoItem>
              <InfoItem>
                <InfoLabel size="xs" color="secondary">{t('profile.isHonoraryMember')}</InfoLabel>
                {renderValue(member.isHonoraryMember ? t('common.yes') : t('common.no'))}
              </InfoItem>
              <InfoItem>
                <InfoLabel size="xs" color="secondary">{t('vignette.statusLabel')}</InfoLabel>
                <VignetteStatusBadge status={vignetteStatus} />
              </InfoItem>
            </InfoGrid>
          </SectionCard>
        );
      case 'professional':
        return (
          <SectionCard variant="elevated" accentBorder="top" pattern="dots" patternIntensity="subtle">
            <SectionTitle variant="h3" as="h2" style={{ fontSize: typography.fontSize.section }}>
              {t('profile.sections.professional')}
            </SectionTitle>
            <ProfessionalProfileDisplay member={member} />
          </SectionCard>
        );
      default:
        return null;
    }
  };

  return (
    <Page>
      <IdentityCard style={watermark.style}>
        <IdentityInner>
          <div style={{ width: '100%' }}>
            <AccentBar aria-hidden />
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: spacing[5],
                width: '100%',
              }}
            >
          <IdentityMain>
            <Avatar name={displayName} size="xl" border borderColor="primary" />
            <IdentityText>
              <Name>{displayName}</Name>
              <MetaRow>
                {member.email && (
                  <MetaItem>
                    <EnvelopeIcon aria-hidden />
                    {member.email}
                  </MetaItem>
                )}
                {(member.phone || member.mobile) && (
                  <MetaItem>
                    <PhoneIcon aria-hidden />
                    {member.mobile || member.phone}
                  </MetaItem>
                )}
              </MetaRow>
              <BadgeRow>
                <SoftBadge $tone="accent">{getMembershipTypeLabel(member.membershipType)}</SoftBadge>
                <VignetteStatusBadge status={vignetteStatus} />
                {member.isActive === false && (
                  <SoftBadge $tone="warning">{t('admin.member.accountInactive')}</SoftBadge>
                )}
              </BadgeRow>
            </IdentityText>
          </IdentityMain>

          <Actions>
            <Button variant="outline" size="sm" onClick={() => navigate('/admin/members')}>
              <ArrowLeftIcon style={{ width: 16, height: 16, marginRight: 6 }} />
              {t('admin.member.backToMembers')}
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={() => id && navigate(`/admin/members/${id}/edit`)}
            >
              <PencilSquareIcon style={{ width: 16, height: 16, marginRight: 6 }} />
              {t('admin.member.edit')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setAccountActionOpen(true)}
            >
              {member.isActive === false
                ? t('admin.member.reactivate')
                : t('admin.member.deactivate')}
            </Button>
          </Actions>
            </div>
          </div>
        </IdentityInner>
        <MonumentCaption nameEn={watermark.nameEn} nameGr={watermark.nameGr} />
      </IdentityCard>

      <Layout>
        <SideNav variant="elevated" pattern="dots" patternIntensity="subtle">
          <NavList aria-label={t('profile.title')}>
            {sections.map(section => (
              <NavButton
                key={section.id}
                type="button"
                $active={activeSection === section.id}
                onClick={() => selectSection(section.id)}
                aria-current={activeSection === section.id ? 'page' : undefined}
              >
                {section.label}
              </NavButton>
            ))}
          </NavList>
        </SideNav>
        <div>{renderSection()}</div>
      </Layout>

      <ConfirmDialog
        isOpen={accountActionOpen}
        onClose={() => !accountActionLoading && setAccountActionOpen(false)}
        onConfirm={handleAccountAction}
        title={
          member.isActive === false
            ? t('admin.member.reactivate')
            : t('admin.member.deactivate')
        }
        message={
          member.isActive === false
            ? t('admin.member.reactivateConfirm')
            : t('admin.member.deactivateConfirm')
        }
        confirmText={
          member.isActive === false
            ? t('admin.member.reactivate')
            : t('admin.member.deactivate')
        }
        variant={member.isActive === false ? 'primary' : 'danger'}
      />
    </Page>
  );
};

export default MemberProfile;
